import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IndustrySelect } from "@/components/shared/IndustrySelect";
import { formatPhoneLive } from "@/lib/phone";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

// Generic form modal driven by a field config array.
export function FormModal({ open, onOpenChange, title, fields, initial, onSubmit, submitLabel = "Save", testid }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => { base[f.name] = initial?.[f.name] ?? f.default ?? (f.type === "multiselect" ? [] : ""); });
      setValues(base);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (n, v) => setValues((s) => ({ ...s, [n]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
              <FieldInput field={f} value={values[f.name]} onChange={(v) => set(f.name, v)} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="form-modal-submit">{saving ? "Saving…" : submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ field, value, onChange }) {
  const tid = `field-${field.name}`;
  if (field.type === "textarea")
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder={field.placeholder} />;
  if (field.type === "number")
    return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={tid} />;
  if (field.type === "date")
    return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (field.type === "phone")
    return <Input inputMode="tel" value={value || ""} onChange={(e) => onChange(formatPhoneLive(e.target.value))} data-testid={tid} placeholder={field.placeholder || "(555) 123-4567"} />;
  if (field.type === "color")
    return <div className="flex items-center gap-2"><input type="color" value={value || "#4f46e5"} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-9 w-14 rounded border cursor-pointer" /><span className="text-xs text-slate-500 font-mono">{value || "#4f46e5"}</span></div>;
  if (field.type === "checkbox")
    return <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} />{field.hint || "Yes"}</label>;
  if (field.type === "industry")
    return <IndustrySelect value={value || null} onChange={onChange} allowClear placeholder={field.placeholder} />;
  if (field.type === "select")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => { const opt = typeof o === "string" ? { value: o, label: o } : o; return <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>; })}
        </SelectContent>
      </Select>
    );
  if (field.type === "multiselect") {
    const arr = value || [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div className="flex flex-wrap gap-2" data-testid={tid}>
        {field.options.map((o) => (
          <button type="button" key={o.value} onClick={() => toggle(o.value)}
            className={`px-3 py-1 rounded-full text-xs border capitalize transition-colors ${arr.includes(o.value) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder={field.placeholder} />;
}
